import { useChecklistStore } from "../Stores/Checklist";
import { useFormStore } from "../Stores/Form";
import { useHelpCenterStore } from "../Stores/HelpCenter";
import { useRouterStore } from "../Stores/Router";
import { useSurveyV2Store } from "../Stores/SurveyV2";
import { useTicketStore } from "../Stores/Ticket";
import Communicator from "./Communicator";
import CommunicationManager from "./CommunicationManager";
import { isSalesDemoMode, openSalesDemoUrl } from "../salesDemo/protocol";

const openLegacySurvey = (identifier: string) => {
  useFormStore.setState({
    feedbackFlow: identifier,
    isSurveyFlow: true,
  });
  useRouterStore.getState().pushPage("flow", {
    flowId: identifier,
  });
};

/**
 * gleap://survey/<id> inside the messenger (chat messages, articles, news):
 * a Surveys 2.0 survey opens in the 2.0 renderer as a messenger page (header
 * with back, closing returns to where the link was); a
 * legacy one (GET /surveys/:id is no 200) opens the legacy form as before.
 */
const openSurveyLink = (identifier?: string) => {
  if (!identifier) return;
  const surveyId = decodeURIComponent(identifier.split(/[?#]/)[0]);
  useRouterStore.getState().pushPage("survey-inline", { surveyId });
  useSurveyV2Store
    .getState()
    .load({ surveyId, format: "page", channel: "web", inMessenger: true })
    .then((result) => {
      const router = useRouterStore.getState();
      if (result === "legacy" && router.currentPage === "survey-inline" && router.currentData?.surveyId === surveyId) {
        router.popPage();
        openLegacySurvey(surveyId);
      }
    })
    .catch(() => openLegacySurvey(surveyId));
};

export const handleGleapLink = (href: string, target: boolean) => {
  if (isSalesDemoMode()) {
    openSalesDemoUrl(href);
    return false;
  }
  // Check if element.href starts with gleap:
  if (href.startsWith("gleap:")) {
    try {
      const urlParts = href.split("/");
      const type = urlParts[2];

      if (type === "flow") {
        const identifier = urlParts[3];
        useFormStore.setState({
          feedbackFlow: identifier,
          isSurveyFlow: false,
        });
        useRouterStore.getState().pushPage("flow", {
          flowId: identifier,
        });

        return false;
      }

      if (type === "bot") {
        const identifier = urlParts[3];
        useTicketStore.getState().createNewConversation({
          botId: identifier,
        });
        return false;
      }

      if (type === "article") {
        const identifier = urlParts[3];
        useHelpCenterStore.getState().openArticle(identifier);

        return false;
      }

      if (type === "action") {
        const identifier = urlParts[3];

        Communicator.runCustomAction(
          identifier,
          useTicketStore.getState()?.currentTicket?.shareToken,
        );

        if (true) {
          Communicator.closeWidget();
        }

        return false;
      }

      if (type === "collection") {
        const identifier = urlParts[3];
        useRouterStore.getState().pushPage("helpcenter-collection", {
          id: identifier,
        });
        return false;
      }

      if (type === "checklist") {
        const identifier = urlParts[3];
        useRouterStore.getState().pushPage("checklists");
        const sharedKey = urlParts[4] || undefined;
        useChecklistStore.getState().startChecklist(identifier, sharedKey);

        return false;
      }

      if (type === "survey") {
        openSurveyLink(urlParts[3]);
        return false;
      }

      if (type === "tour") {
        const identifier = urlParts[3];
        // Product tours run on the host page, not inside the widget iframe, so
        // this has to go to the SDK. It must NOT call startChecklist — a tour id
        // is not an outboundId.
        CommunicationManager.getInstance().sendMessage({
          name: "start-product-tour",
          data: {
            tourId: identifier,
          },
        });
        Communicator.closeWidget();

        return false;
      }

      if (type === "news") {
        const identifier = urlParts[3];
        useRouterStore.getState().pushPage("newsdetails", {
          id: identifier,
        });
        return false;
      }
    } catch (e) {
      console.error("Failed to handle Gleap link: ", href);
    }

    // An unrecognised gleap:// type is a typo, not a web address. Handing it to
    // openURL would ask the host page to navigate to an unknown scheme, so stop
    // here instead.
    return false;
  }

  // Links inside messages, articles and news: never fill session placeholders.
  Communicator.openURL(href, target, { placeholders: false });
  return false;
};
